import moment from "moment";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AgeIcon from "../../../../assets/images/Patient_calendar.svg";
import PhoneIcon from "../../../../assets/images/Patient_phone-outline.svg";
import { ProfileAvatar } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { useGetData } from "../../../../hooks";
import { GetPatientInfo } from "../../../../interface/GeneralResponse";
import { RootState } from "../../../../redux/store";
import { NotificationsLoader } from "../../../../Share";
import { formatAge, getGenderEmoji } from "../../../../utils/utils";
import { setMedicalRecord } from "../../../../redux/reducers/medicalRecordSlice";

const PatientInfo = ({
  layout,
  className = "",
  Id,
  totalVisibleKeys,
  getMedicalTemplateQuery
}: {
  layout?: "sidebar";
  className?: string;
  Id?: string | number;
  totalVisibleKeys?: any
  getMedicalTemplateQuery?: any
}) => {
  const { PatientId: ParamsPatientId,templateId } = useParams();

  const { t } = useTranslation();

  const PatientId = Id ?? ParamsPatientId;

  const { data, isLoading, isSuccess, isFetched } = useGetData<GetPatientInfo>(
    EndPoints.GetPatients,
    {
      // refetchOnWindowFocus: true,
      // staleTime: 0,
      enabled: !!PatientId,
    },
    PatientId,
  );

  function useOptionalFormContext() {
    try {
      return useFormContext();
    } catch {
      return null;
    }
  }

  const form = useOptionalFormContext();
  const reset = form?.reset;
  const getValues = form?.getValues;
  const dispatch = useDispatch();

  const patient = data?.Data;

  const enteredValues = getValues && getValues();

  const genderVisible = totalVisibleKeys?.includes("Gender");

    useEffect(() => {
       if (!isSuccess || genderVisible === undefined) return;
    if (isSuccess && !enteredValues?.Gender) {

         
      const {
        Allergies,
        Birthday,
        BloodType,
        Gender,
        FamilyHistory,
        ChronicDiseases,
        Weight,
      } = patient as any;

      const defaultData = {
        ...enteredValues,
        PatientId: patient?.Id,
        Birthday,
        BloodType,
        Gender:totalVisibleKeys?.includes("Gender") ?Gender:undefined,
        Weight,
        Length: patient?.Height,
        MedicalHistoryDate: moment().locale("en").format("DD-MM-YYYY HH:mm:ss"),
        // IsPregnancy: patient?.Pregnant ?? Gender?.toLowerCase() === "female" ? "false" : ""
        IsPregnancy:
        // totalVisibleKeys?.includes("Gender") &&
          Gender?.toLowerCase() === "female"
            ? patient?.Pregnant
              ? "true"
              : null
            : null,
      };

      reset && reset(defaultData);

      dispatch(setMedicalRecord(defaultData as any));
    }
  }, [isSuccess, isFetched,genderVisible]);

  const UserInfo = useSelector((state: RootState) => state.registration);

  const Gender = t(patient?.Gender?.trim()?.toLowerCase() ?? "");
  const Age = formatAge(patient?.Birthday || "", true) + " " + t("years_old");
  const PhoneNumber =
    UserInfo.userDetails.Country?.Code + "-" + patient?.PhoneNumber;

  return (
    <div
      className={`mx-auto mb-3 flex ${layout == "sidebar" ? "h-full flex-col gap-6" : "w-full"} flex-nowrap gap-2 rounded-2xl bg-[#F4F7FE] p-4 ${className}`}
    >
      {isLoading ? (
        <NotificationsLoader />
      ) : (
        <>
          {layout === "sidebar" && (
            <h2 className="text-lg font-bold">{t("patient_info")}</h2>
          )}
          <div className="flex items-center gap-2">
            <ProfileAvatar
              Id={patient?.Id}
              FullName={patient?.FullName}
              src={patient?.Image}
              alt="profile"
              size={"small"}
              className="h-12 w-12 flex-none rounded-full object-cover"
              height="2.5rem"
              width="2.5rem"
            />
            {layout === "sidebar" && (
              <p className="text-primary-text line-clamp-1 w-32 whitespace-nowrap text-lg font-bold lg:w-full">
                {patient?.FullName}
              </p>
            )}
          </div>
          {layout !== "sidebar" && (
            <div className="">
              <p className="text-primary-text line-clamp-1 whitespace-nowrap text-lg font-bold">
                {patient?.FullName}
              </p>

              <>
                <p className="text-body-text flex flex-wrap gap-2">
                  {patient?.Gender && (
                    <span>
                      {getGenderEmoji(patient?.Gender)} {Gender}
                    </span>
                  )}
                  <span>• </span>
                  {patient?.Birthday && <span> {Age}</span>}
                  <span>• </span>
                  <span dir="ltr" className="text-body-text rtl:text-end">
                    {PhoneNumber}
                  </span>
                </p>
              </>
            </div>
          )}
        </>
      )}

      {layout === "sidebar" && !isLoading && (
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-2 rounded-lg bg-white p-2">
            <div className="flex gap-1">
              <span> {getGenderEmoji(patient?.Gender ?? "")} </span>
              <p>{t("gender")}</p>
            </div>
            <p>{Gender}</p>
          </div>
          <div className="grid grid-cols-2 p-2">
            <div className="flex gap-1">
              <img alt="age" src={AgeIcon} />
              <p>{t("age")}</p>
            </div>
            <p>{Age}</p>
          </div>
          <div className="grid grid-cols-2 rounded-lg bg-white p-2">
            <div className="flex gap-1">
              <img className="h-6 w-6" alt="Phone" src={PhoneIcon} />
              <p>{t("phone_number")}</p>
            </div>
            <p dir="ltr">{PhoneNumber}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientInfo;
